import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Mail, Phone, MessageCircle, Calendar, Sparkles, Activity, Linkedin, ArrowDownLeft, ArrowUpRight } from 'lucide-react';

const CHANNELS = {
  email: { icon: Mail, label: 'Email', color: 'text-blue-500', bg: 'bg-blue-500/10 border-blue-500/30' },
  voice: { icon: Phone, label: 'Voice', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'text-green-500', bg: 'bg-green-500/10 border-green-500/30' },
  meeting: { icon: Calendar, label: 'Meetings', color: 'text-amber-500', bg: 'bg-amber-500/10 border-amber-500/30' },
  brain: { icon: Sparkles, label: 'Brain', color: 'text-violet-500', bg: 'bg-violet-500/10 border-violet-500/30' },
  linkedin: { icon: Linkedin, label: 'LinkedIn', color: 'text-sky-500', bg: 'bg-sky-500/10 border-sky-500/30' },
  system: { icon: Activity, label: 'System', color: 'text-muted-foreground', bg: 'bg-muted border-border' },
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const diff = Math.floor((today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined });
};

export const LeadJourneyTimeline = ({ leadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/api/leads/${leadId}/journey`)
      .then((res) => { if (live) setData(res.data); })
      .catch(() => { if (live) setData({ events: [], channel_counts: {} }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [leadId]);

  const groups = useMemo(() => {
    const events = (data?.events || []).filter((e) => filter === 'all' || e.channel === filter);
    const byDay = [];
    let current = null;
    for (const ev of events) {
      const label = dayLabel(ev.ts);
      if (!current || current.label !== label) { current = { label, items: [] }; byDay.push(current); }
      current.items.push(ev);
    }
    return byDay;
  }, [data, filter]);

  if (loading) {
    return <div className="space-y-3" data-testid="journey-loading">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  }

  const counts = data?.channel_counts || {};
  const chips = ['all', ...Object.keys(CHANNELS).filter((c) => counts[c])];

  return (
    <div data-testid="lead-journey-timeline">
      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        {chips.map((c) => {
          const conf = CHANNELS[c];
          const Icon = conf?.icon;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`journey-filter-${c}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === c ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}
            >
              {Icon && <Icon className="h-3 w-3" />}
              {c === 'all' ? `All (${data?.total || 0})` : `${conf.label} (${counts[c]})`}
            </button>
          );
        })}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="journey-empty">
          No journey events yet — outreach, calls and replies will appear here.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.label} className="mb-5">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{g.label}</div>
          <div className="relative pl-5 border-l-2 border-border space-y-3">
            {g.items.map((ev) => {
              const conf = CHANNELS[ev.channel] || CHANNELS.system;
              const Icon = conf.icon;
              return (
                <div key={ev.id} className="relative" data-testid="journey-event">
                  <div className={`absolute -left-[27px] top-1 h-4 w-4 rounded-full border flex items-center justify-center ${conf.bg}`}>
                    <Icon className={`h-2.5 w-2.5 ${conf.color}`} />
                  </div>
                  <div className="bg-card border border-border rounded-lg px-3.5 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {ev.direction === 'inbound' && <ArrowDownLeft className="h-3 w-3 text-emerald-500 shrink-0" />}
                        {ev.direction === 'outbound' && <ArrowUpRight className="h-3 w-3 text-blue-500 shrink-0" />}
                        <span className="text-sm font-medium truncate">{ev.title}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {ev.meta?.status && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{ev.meta.status}</Badge>}
                        <span className="text-[11px] text-muted-foreground">{new Date(ev.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    {ev.snippet && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ev.snippet}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
